<template>
  <div v-if="isActive" class="onboarding-tour">
    <!-- Backdrop -->
    <div class="tour-backdrop" @click="handleBackdropClick"></div>

    <!-- Highlight overlay -->
    <div
      v-if="currentStep && currentStep.target"
      class="tour-highlight"
      :style="highlightStyle"
    ></div>

    <!-- Tour tooltip -->
    <div
      v-if="currentStep"
      class="tour-tooltip"
      :style="tooltipStyle"
      :class="tooltipClass"
    >
      <div class="tooltip-header">
        <h4 v-if="currentStep.title" class="tooltip-title">{{ currentStep.title }}</h4>
        <button class="close-button" @click="closeTour">
          <i class="el-icon-close"></i>
        </button>
      </div>

      <div class="tooltip-content">
        <p class="tooltip-description">{{ currentStep.description }}</p>

        <div v-if="currentStep.tips" class="tooltip-tips">
          <ul>
            <li v-for="tip in currentStep.tips" :key="tip">{{ tip }}</li>
          </ul>
        </div>
      </div>

      <div class="tooltip-actions">
        <el-button
          v-if="currentStepIndex > 0"
          @click="previousStep"
          type="text"
        >
          Previous
        </el-button>

        <div class="action-buttons">
          <el-button @click="skipTour" type="text">Skip Tour</el-button>

          <el-button
            v-if="currentStepIndex < tourSteps.length - 1"
            @click="nextStep"
            type="primary"
          >
            Next
          </el-button>

          <el-button
            v-else
            @click="completeTour"
            type="primary"
          >
            Start Searching!
          </el-button>
        </div>
      </div>
    </div>

    <!-- Progress dots -->
    <div class="tour-progress">
      <div
        v-for="(step, index) in availableSteps"
        :key="step.id"
        class="progress-dot"
        :class="{
          'active': index === currentStepNumber,
          'completed': index < currentStepNumber
        }"
      ></div>
    </div>
  </div>
</template>

<script>
import { trackOnboardingStep, markTourAsTaken } from '../../utils/onboarding';

export default {
  name: 'OnboardingTour',
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    autoStart: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      currentStepIndex: 0,
      highlightStyle: {},
      tooltipStyle: {},
      tooltipClass: '',

      tourSteps: [
        {
          id: 'welcome',
          title: 'Welcome to Your Sports Portal!',
          description: 'This is your dashboard - your home base for discovering and registering for sports events and memberships in your area.',
          target: null, // No specific target, general welcome
          position: 'center'
        },
        {
          id: 'featured',
          title: 'Popular Events',
          description: 'These are the most popular events and memberships in your area right now. Click on any card to see more details and register.',
          target: '#featured-section',
          position: 'bottom',
          tips: [
            'Featured events change regularly based on what\'s popular',
            'Click "Register" to see available options for that event'
          ]
        },
        {
          id: 'search',
          title: 'Search Everything',
          description: 'Use this search button to browse all available events and memberships. This is your main way to find what you\'re looking for.',
          target: '.search-button',
          position: 'top',
          tips: [
            'Search shows all available options, not just featured ones',
            'You can filter results by sport, location, and more'
          ]
        },
        {
          id: 'search-options',
          title: 'Search Options',
          description: 'Use these filters to narrow down your search by sport type, location, dates, and more. Customize your search to find exactly what you need.',
          target: '.search-options-container',
          position: 'top',
          tips: [
            'Apply multiple filters to refine results',
            'Clear filters anytime to see all options',
            'Filters help you find the perfect match'
          ]
        },
        {
          id: 'personalization',
          title: 'Get Personalised Results',
          description: 'Answer these questions to see options that match your interests. This helps us show you the most relevant events first.',
          target: '.tile-container',
          position: 'right',
          tips: [
            'You can change these preferences anytime',
            'More specific selections = better recommendations'
          ]
        },
        {
          id: 'browse-results',
          title: 'Browse Results',
          description: 'All available events and memberships appear here. Each card shows key details like dates, location, and pricing.',
          target: '.browse-results-container',
          position: 'top',
          tips: [
            'Click on any card to see full details',
            'Look for "Register" buttons to sign up',
            'Scroll down to see more results'
          ]
        },
        {
          id: 'member-management',
          title: 'Manage Your Members',
          description: 'This panel is where you manage all your member profiles. You can create new members, view existing ones, and register them for events. Each family member gets their own profile here.',
          target: '.member-board', // Will be updated dynamically
          position: 'left',
          tips: [
            'Click "Add Member" to create new member profiles',
            'Each family member gets their own profile',
            'View registration history and upcoming events for each member',
            'Easy renewal notifications for memberships'
          ]
        }
      ]
    };
  },
  computed: {
    currentStep() {
      return this.tourSteps[this.currentStepIndex];
    },
    availableSteps() {
      // Filter out steps whose target elements don't exist
      return this.tourSteps.filter((step) => {
        if (!step.target) return true; // Steps without targets are always available

        let targetSelector = step.target;
        if (step.id === 'member-management') {
          targetSelector = this.windowWidth <= 992 ? '#member-board-mobile' : '#member-board-desktop';
        }

        const element = document.querySelector(targetSelector);
        let isAvailable = element && element.offsetParent !== null;

        // Special check for featured section - check if it has the carousel content
        if (step.id === 'featured' && element) {
          const carousel = element.querySelector('.featured-container');
          isAvailable = carousel && carousel.offsetParent !== null;
        }

        return isAvailable;
      });
    },
    currentStepNumber() {
      // Find the current step's position in available steps
      const { availableSteps } = this;
      const { currentStep } = this;
      return availableSteps.findIndex((step) => step.id === currentStep.id);
    }
  },
  watch: {
    isActive(newVal) {
      if (newVal) {
        this.startTour();
      } else {
        this.resetTour();
      }
    },
    currentStepIndex() {
      this.$nextTick(() => {
        this.updateHighlight();
      });
    }
  },
  mounted() {
    if (this.autoStart && this.isActive) {
      this.startTour();
    }

    // Listen for window resize to update highlight positions and window width
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.resetTour();
  },
  methods: {
    startTour() {
      this.currentStepIndex = 0;
      document.body.style.overflow = 'hidden';
      this.$nextTick(() => {
        this.skipUnavailableSteps();
        this.updateHighlight();
      });
    },

    resetTour() {
      this.currentStepIndex = 0;
      document.body.style.overflow = '';
      this.highlightStyle = {};
      this.tooltipStyle = {};
    },

    nextStep() {
      if (this.currentStepIndex < this.tourSteps.length - 1) {
        const { currentStep } = this;
        trackOnboardingStep(currentStep.id, 'step_completed');
        this.currentStepIndex++;
        this.skipUnavailableSteps();
      }
    },

    previousStep() {
      if (this.currentStepIndex > 0) {
        trackOnboardingStep(this.currentStep.id, 'step_back');
        this.currentStepIndex--;
        this.skipUnavailableSteps('backward');
      }
    },

    goToStep(index) {
      if (index >= 0 && index < this.tourSteps.length) {
        trackOnboardingStep(this.currentStep.id, 'step_jumped');
        this.currentStepIndex = index;
      }
    },

    skipTour() {
      this.$emit('tour-skipped');
      this.closeTour();
    },

    async completeTour() {
      // Mark onboarding as completed locally
      localStorage.setItem('onboarding_completed', 'true');

      // Call API to mark tour as taken
      await markTourAsTaken(this);

      this.$emit('tour-completed');
      this.closeTour();
    },

    closeTour() {
      this.$emit('tour-closed');
      document.body.style.overflow = '';
    },

    handleBackdropClick() {
      // Optional: close tour on backdrop click or do nothing
      // this.closeTour();
    },

    skipUnavailableSteps(direction = 'forward') {
      // Check if current step's target element exists
      const step = this.tourSteps[this.currentStepIndex];

      if (!step.target) {
        // No target means it's always available (like welcome step)
        return;
      }

      let targetSelector = step.target;
      // Update target for member board based on screen size
      if (step.id === 'member-management') {
        targetSelector = this.windowWidth <= 992 ? '#member-board-mobile' : '#member-board-desktop';
      }

      const element = document.querySelector(targetSelector);

      // Check if element is available and has visible content
      let isAvailable = element && element.offsetParent !== null;

      // Special check for featured section - check if it has the carousel content
      if (step.id === 'featured' && element) {
        const carousel = element.querySelector('.featured-container');
        isAvailable = carousel && carousel.offsetParent !== null;
      }

      // If element doesn't exist, is hidden, or has no content, skip this step
      if (!isAvailable) {
        console.log(`Skipping step "${step.id}" - target not available`);

        if (direction === 'forward') {
          if (this.currentStepIndex < this.tourSteps.length - 1) {
            this.currentStepIndex++;
            this.skipUnavailableSteps(direction); // Recursively check next step
          }
        } else if (this.currentStepIndex > 0) {
          this.currentStepIndex--;
          this.skipUnavailableSteps(direction); // Recursively check previous step
        }
      }
    },

    scrollElementIntoView(element) {
      if (!element || !(element instanceof Element)) {
        console.warn('scrollElementIntoView called with invalid element:', element);
        return;
      }
      if (typeof element.getBoundingClientRect !== 'function') return;

      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const isVisible = (
        rect.top >= 0
        && rect.left >= 0
        && rect.bottom <= viewportHeight
        && rect.right <= viewportWidth
      );

      if (!isVisible) {
        const scrollOptions = {
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        };

        const fallbackScroll = () => {
          const latestRect = element.getBoundingClientRect();
          const targetTop = latestRect.top + window.pageYOffset - (viewportHeight / 2) + (latestRect.height / 2);
          window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: 'smooth'
          });
        };

        try {
          if (typeof element.scrollIntoView === 'function') {
            element.scrollIntoView(scrollOptions);
          } else {
            fallbackScroll();
          }
        } catch (error) {
          console.warn('scrollIntoView failed, using manual scroll', error);
          fallbackScroll();
        }

        setTimeout(() => {
          this.updateHighlightPosition();
        }, 500);
      }
    },

    updateHighlightPosition() {
      // This method updates just the highlight position without scrolling
      const step = this.currentStep;
      if (!step.target) return;

      let targetSelector = step.target;
      if (step.id === 'member-management') {
        targetSelector = this.windowWidth <= 992 ? '#member-board-mobile' : '#member-board-desktop';
      }

      const element = document.querySelector(targetSelector);
      if (!element || typeof element.getBoundingClientRect !== 'function') return;

      const rect = element.getBoundingClientRect();
      const padding = 8;

      this.highlightStyle = {
        position: 'fixed',
        top: `${rect.top - padding}px`,
        left: `${rect.left - padding}px`,
        width: `${rect.width + (padding * 2)}px`,
        height: `${rect.height + (padding * 2)}px`,
        zIndex: 10001
      };
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      this.updateHighlight();
    },

    updateHighlight() {
      const step = this.currentStep;

      if (!step.target) {
        // Center the tooltip for steps without targets
        this.highlightStyle = {};
        this.tooltipStyle = {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10002
        };
        this.tooltipClass = 'tooltip-center';
        return;
      }

      // Update target based on current screen size for member board step
      let targetSelector = step.target;
      if (step.id === 'member-management') {
        targetSelector = this.windowWidth <= 992 ? '#member-board-mobile' : '#member-board-desktop';
      }

      const element = document.querySelector(targetSelector);
      if (!element || typeof element.getBoundingClientRect !== 'function') {
        console.warn(`Tour target not found: ${targetSelector}`);
        return;
      }

      // Scroll element into view if needed
      this.scrollElementIntoView(element);

      const rect = element.getBoundingClientRect();
      const padding = 8;

      // Highlight style
      this.highlightStyle = {
        position: 'fixed',
        top: `${rect.top - padding}px`,
        left: `${rect.left - padding}px`,
        width: `${rect.width + (padding * 2)}px`,
        height: `${rect.height + (padding * 2)}px`,
        zIndex: 10001
      };

      // Tooltip positioning with better viewport boundary detection
      const tooltipOffset = 20;
      const tooltipWidth = 500; // Increased from 400
      const tooltipHeight = 280; // Reduced height since we're making it wider
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const topPadding = 100; // Increased padding from top
      const bottomPadding = 50; // Increased padding from bottom
      const sidePadding = 20;

      let tooltipTop;
      let tooltipLeft;
      let tooltipClass = 'tooltip-';

      // Update position based on current screen size for specific steps
      const { position: stepPosition, id } = step;
      let position = stepPosition;
      if (id === 'member-management') {
        position = this.windowWidth <= 992 ? 'bottom' : 'left';
      }
      // Adjust positions for better mobile display
      if (this.windowWidth <= 768) {
        if (position === 'top' && id !== 'search') {
          position = 'bottom'; // Avoid top positioning on mobile to prevent cutoff
        }
      }

      // Pre-calculate safe boundaries
      const safeTop = topPadding;
      const safeBottom = viewportHeight - tooltipHeight - bottomPadding;
      const safeLeft = sidePadding + (tooltipWidth / 2);
      const safeRight = viewportWidth - sidePadding - (tooltipWidth / 2);

      // Calculate initial position based on preferred placement
      switch (position) {
        case 'top':
          tooltipTop = rect.top - tooltipHeight - tooltipOffset;
          tooltipLeft = rect.left + (rect.width / 2);
          tooltipClass += 'top';
          break;
        case 'bottom':
          tooltipTop = rect.bottom + tooltipOffset;
          tooltipLeft = rect.left + (rect.width / 2);
          tooltipClass += 'bottom';
          break;
        case 'left':
          tooltipTop = rect.top + (rect.height / 2) - (tooltipHeight / 2);
          tooltipLeft = rect.left - tooltipWidth - tooltipOffset;
          tooltipClass += 'left';
          break;
        case 'right':
          tooltipTop = rect.top + (rect.height / 2) - (tooltipHeight / 2);
          tooltipLeft = rect.right + tooltipOffset;
          tooltipClass += 'right';
          break;
        default:
          tooltipTop = rect.bottom + tooltipOffset;
          tooltipLeft = rect.left + (rect.width / 2);
          tooltipClass += 'bottom';
      }

      // Vertical boundary checks and fallback positioning
      if (tooltipTop < safeTop) {
        // Try positioning below
        if (rect.bottom + tooltipOffset + tooltipHeight <= viewportHeight - bottomPadding) {
          tooltipTop = rect.bottom + tooltipOffset;
          tooltipClass = 'tooltip-bottom';
        } else {
          // Center vertically in safe area
          tooltipTop = Math.max(safeTop, Math.min(safeBottom, rect.top + (rect.height / 2) - (tooltipHeight / 2)));
          tooltipClass = 'tooltip-center';
        }
      } else if (tooltipTop > safeBottom) {
        // Try positioning above
        if (rect.top - tooltipHeight - tooltipOffset >= safeTop) {
          tooltipTop = rect.top - tooltipHeight - tooltipOffset;
          tooltipClass = 'tooltip-top';
        } else {
          // Center vertically in safe area
          tooltipTop = Math.max(safeTop, Math.min(safeBottom, rect.top + (rect.height / 2) - (tooltipHeight / 2)));
          tooltipClass = 'tooltip-center';
        }
      }

      // Horizontal boundary checks for all positions
      if (tooltipClass.includes('left') || tooltipClass.includes('right')) {
        // For left/right positioning, adjust left position if needed
        if (tooltipClass.includes('left') && tooltipLeft < sidePadding) {
          // Switch to right if there's space
          if (rect.right + tooltipWidth + tooltipOffset <= viewportWidth - sidePadding) {
            tooltipLeft = rect.right + tooltipOffset;
            tooltipClass = 'tooltip-right';
          } else {
            // Center horizontally
            tooltipLeft = (viewportWidth - tooltipWidth) / 2;
            tooltipClass = 'tooltip-center';
          }
        } else if (tooltipClass.includes('right') && tooltipLeft + tooltipWidth > viewportWidth - sidePadding) {
          // Switch to left if there's space
          if (rect.left - tooltipWidth - tooltipOffset >= sidePadding) {
            tooltipLeft = rect.left - tooltipWidth - tooltipOffset;
            tooltipClass = 'tooltip-left';
          } else {
            // Center horizontally
            tooltipLeft = (viewportWidth - tooltipWidth) / 2;
            tooltipClass = 'tooltip-center';
          }
        }
      } else {
        // For top/bottom/center positioning, ensure horizontal centering is within bounds
        tooltipLeft = Math.max(safeLeft, Math.min(safeRight, tooltipLeft));
      }

      // Final safety check - ensure tooltip is fully visible
      tooltipTop = Math.max(safeTop, Math.min(safeBottom, tooltipTop));
      tooltipLeft = Math.max(sidePadding, Math.min(viewportWidth - tooltipWidth - sidePadding, tooltipLeft));

      this.tooltipStyle = {
        position: 'fixed',
        top: `${tooltipTop}px`,
        left: `${tooltipLeft}px`,
        zIndex: 10002
      };

      this.tooltipClass = tooltipClass;
    }
  }
};
</script>

<style lang="scss" scoped>
.onboarding-tour {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  pointer-events: none;

  * {
    pointer-events: auto;
  }
}

.tour-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10000;
  pointer-events: auto;
}

.tour-highlight {
  border: 3px solid var(--highlight-color, #409eff);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
  transition: all 0.3s ease;
}

.tour-tooltip {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: 0;
  max-width: 500px;
  min-width: 400px;
  width: 500px;
  transition: all 0.3s ease;

  &.tooltip-center {
    transform: translate(-50%, -50%);
  }

  &.tooltip-top {
    transform: translate(-50%, -100%);
    margin-top: -10px;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 10px solid transparent;
      border-top-color: white;
    }
  }

  &.tooltip-bottom {
    transform: translateX(-50%);
    margin-top: 10px;

    &::before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 10px solid transparent;
      border-bottom-color: white;
    }
  }

  &.tooltip-left {
    transform: translate(-100%, -50%);
    margin-left: -10px;

    &::after {
      content: '';
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      border: 10px solid transparent;
      border-left-color: white;
    }
  }

  &.tooltip-right {
    transform: translateY(-50%);
    margin-left: 10px;

    &::before {
      content: '';
      position: absolute;
      right: 100%;
      top: 50%;
      transform: translateY(-50%);
      border: 10px solid transparent;
      border-right-color: white;
    }
  }
}

.tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;

  .tooltip-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #333;
    margin: 0;
    flex: 1;
  }

  .close-button {
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-left: 1rem;

    &:hover {
      background: #f5f5f5;
      color: #666;
    }
  }
}

.tooltip-content {
  padding: 1rem 1.5rem;
  max-height: 300px;
  overflow-y: auto;

  .tooltip-description {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #666;
    margin: 0 0 0.75rem 0;
  }

  .tooltip-tips {
    ul {
      margin: 0;
      padding-left: 1.2rem;

      li {
        font-size: 0.85rem;
        color: #777;
        margin-bottom: 0.35rem;
        line-height: 1.5;
      }
    }
  }
}

.tooltip-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid #f0f0f0;

  > .el-button {
    // Previous button styling (when visible)
    align-self: center;
  }

  .action-buttons {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }
}

.tour-progress {
  position: fixed;
  bottom: 30px;
  left: 20px;
  display: flex;
  gap: 0.5rem;
  z-index: 10003;

  .progress-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: all 0.3s ease;

    &.active {
      background: var(--highlight-color, #409eff);
      transform: scale(1.2);
    }

    &.completed {
      background: rgba(255, 255, 255, 0.8);
    }

    &:hover {
      background: var(--highlight-color, #409eff);
      opacity: 0.8;
    }
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .tour-tooltip {
    max-width: 90vw;
    min-width: auto;
    margin: 1rem;

    &.tooltip-top,
    &.tooltip-bottom {
      left: 50% !important;
      transform: translateX(-50%) !important;
    }

    &.tooltip-left,
    &.tooltip-right {
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      margin: 0;
    }

    &.tooltip-center {
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      margin: 0;
      max-height: 80vh;
      overflow-y: auto;
    }
  }

  .tooltip-header {
    padding: 0.75rem 1rem;

    .tooltip-title {
      font-size: 1.1rem;
    }
  }

  .tooltip-content {
    padding: 0.75rem 1rem;

    .tooltip-description {
      font-size: 0.95rem;
    }
  }

  .tooltip-actions {
    padding: 0.75rem 1rem;
    flex-direction: row;
    gap: 0.5rem;

    > .el-button {
      flex-shrink: 0;
    }

    .action-buttons {
      flex: 1;
      justify-content: flex-end;
    }
  }
}
</style>
